import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Form as FormikForm, Formik, useFormikContext,
} from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';

import media from '../../../utils/mediaQueries';
import Btn from '../../buttons/Btn';
import Field from '../../fields/Field';
import { colors, spacing } from '../tokens';
import {
  AddressInfo, CivilState, Consents, ContactInfo, DemarcheDocument, DemarcheExtraField, ExtraFieldValues,
  TypeAdresse, UploadedDocument,
} from '../types';
import { Country } from './fields/CountryField';
import {
  CountryField, DynamicFieldsForm, isRequired, useCountries,
} from './fields';
import { FieldLabel, RadioChoice, RadioChoices } from './fields/styles';
import {
  Actions, ErrorBanner, Panel, StepText, StepTitle,
} from './styles';
import { isMinorBirthDate } from './submitMapping';

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 800;
  color: ${colors.text};
  margin: ${spacing.xl} 0 ${spacing.md};

  &:first-of-type {
    margin-top: ${spacing.lg};
  }
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 ${spacing.md};

  ${media.mobile`
    grid-template-columns: 1fr;
  `}
`;

const FullWidthRow = styled.div`
  grid-column: 1 / -1;
  margin-bottom: 3px;
`;

const EditLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: -${spacing.sm} 0 ${spacing.md};
  color: ${colors.brand};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: block;
`;

const FieldError = styled.div`
  font-size: 12px;
  margin-top: ${spacing.xs};
  color: #f74656;

  &:empty {
    display: none;
  }
`;

const FieldHint = styled.div`
  font-size: 12px;
  color: ${colors.textMuted};
  margin: -${spacing.sm} 0 ${spacing.md};
`;

const SignedInNote = styled.p`
  font-size: 14px;
  color: ${colors.text};
  margin: 0 0 ${spacing.md};
`;

const ExistingEmailNotice = styled.p`
  font-size: 14px;
  color: #f74656;
  margin: 0 0 ${spacing.md};
`;

const SignInToggleLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: -${spacing.sm} 0 ${spacing.lg};
  color: ${colors.brand};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: block;
`;

const SignInBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 ${spacing.md};
  margin-bottom: ${spacing.lg};

  ${media.mobile`
    grid-template-columns: 1fr;
  `}
`;

const ForgotPasswordNote = styled.p`
  grid-column: 1 / -1;
  font-size: 12px;
  color: ${colors.textMuted};
  margin: -${spacing.sm} 0 0;

  a {
    color: ${colors.brand};
  }
`;

const DocumentsIntro = styled.p`
  font-size: 13px;
  color: ${colors.textMuted};
  margin: -${spacing.sm} 0 ${spacing.lg};
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.md};
  padding: ${spacing.md} 0;
  border-bottom: 1px solid ${colors.border};

  ${media.mobile`
    flex-direction: column;
    align-items: stretch;
  `}
`;

const UploadRowLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text};
`;

const UploadRowHint = styled.div`
  font-size: 12px;
  color: ${colors.textMuted};
  margin-top: 2px;
`;

const UploadRowFiles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: ${spacing.sm};
`;

const UploadedFile = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  font-size: 13px;
  color: ${colors.text};
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #f74656;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const UploadRowActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;

  ${media.mobile`
    align-items: stretch;
  `}
`;

// A global \`input[type="file"] { display: block; }\` reset outranks a plain class selector on
// specificity, so this hides it via position instead of \`display: none\`.
const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const MissingDocumentsNotice = styled.div`
  font-size: 13px;
  color: #f74656;
  margin: ${spacing.sm} 0 0;
`;

const ConsentRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};
  font-size: 14px;
  font-weight: 400;
  line-height: 1.45;
  color: ${colors.text};
  cursor: pointer;

  input {
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

const REQUIRED_MESSAGE = 'Ce champ est obligatoire.';
const CONSENT_REQUIRED_MESSAGE = 'Vous devez cocher cette case pour continuer.';
const PASSWORD_TOO_SHORT_MESSAGE = 'Votre mot de passe doit comporter au moins 6 caractères.';
const FORGOT_PASSWORD_URL = '/eleve_exam_code/password/new';

const CGV_URL = '/a-propos/cgv';

const MINOR_MANDATE_SENTENCE = ' Le candidat étant mineur, je coche cette case en qualité de '
  + 'représentant légal et donne ce mandat en son nom.';

const consentItemsFor = (isMinor: boolean): { id: keyof Consents; label: React.ReactNode }[] => [
  {
    id: 'accuracy',
    label: 'Je certifie l’exactitude des informations fournies dans ce dossier.',
  },
  {
    id: 'cgvMandate',
    label: (
      <>
        {'Je reconnais avoir lu et accepté les '}
        <a href={CGV_URL} target="_blank" rel="noopener noreferrer">Conditions générales de vente</a>
        {' et je donne expressément mandat à Vroom France, exploitant VroomVroom.fr, ainsi qu’aux '
          + 'Partenaires autorisés auxquels elle peut confier tout ou partie de la prestation, pour accomplir '
          + 'en mon nom et pour mon compte les actes nécessaires à la démarche administrative sélectionnée.'}
        {isMinor && MINOR_MANDATE_SENTENCE}
      </>
    ),
  },
  {
    id: 'earlyExecution',
    label: 'Je demande expressément le commencement d’exécution de la prestation avant la fin '
      + 'du délai de rétractation de 14 jours, et je reconnais qu’en cas de rétractation après ce '
      + 'commencement, le montant dû sera proportionnel à la prestation déjà exécutée.',
  },
  {
    id: 'independenceFromAnts',
    label: 'Je comprends que VroomVroom est un service privé, distinct de France Titres — ANTS, '
      + 'et que la décision finale appartient à l’administration.',
  },
];

const CONSENT_IDS = consentItemsFor(false).map((item) => item.id);

const ADDRESS_TYPE_OPTIONS: { value: TypeAdresse; label: string }[] = [
  { value: 'domicile', label: 'il s’agit de mon domicile, je dispose d’un justificatif de domicile à mon nom' },
  { value: 'heberge', label: 'je suis hébergé par un tiers (parent, famille, conjoint)' },
];

const KNOWN_COUNTRY_MESSAGE = 'Sélectionnez un pays dans la liste proposée.';

// `hasCountriesError` is true when the country list failed to load: an empty `countries` array is
// then a fetch failure, not "nothing matches", so the known-country test is dropped rather than
// rejecting every value forever (falls back to plain `required`).
const buildAntsSchema = (countries: Country[], hasCountriesError: boolean) => {
  const isKnownCountry = (value?: string): boolean => (
    !value || countries.some((country) => country.name === value)
  );
  const countryField = Yup.string().trim().required(REQUIRED_MESSAGE);

  return Yup.object().shape({
    nationalite: hasCountriesError
      ? countryField
      : countryField.test('is-known-country', KNOWN_COUNTRY_MESSAGE, isKnownCountry),
    dateNaissance: Yup.string().required(REQUIRED_MESSAGE),
    villeNaissance: Yup.string().trim().required(REQUIRED_MESSAGE),
    paysNaissance: hasCountriesError
      ? countryField
      : countryField.test('is-known-country', KNOWN_COUNTRY_MESSAGE, isKnownCountry),
    adresse: Yup.string().trim().required(REQUIRED_MESSAGE),
    complement: Yup.string(),
    cp: Yup.string().trim().matches(/^\d{5}$/, 'Le code postal doit comporter 5 chiffres.').required(REQUIRED_MESSAGE),
    ville: Yup.string().trim().required(REQUIRED_MESSAGE),
    typeAdresse: Yup.string().required(REQUIRED_MESSAGE),
  });
};

type AntsFormValues = CivilState & AddressInfo;

const DOCUMENT_FILE_ACCEPT = 'image/*,.pdf';
const MAX_FILES_PER_DOCUMENT = 2;

// Lives inside <Formik> so it can read `values` through context without lifting civil-status/address
// state out of Formik just for this — fires `onFieldsChange` (debounced by the container/store)
// whenever any field changes, since the document checklist depends on several of them.
const ChecklistWatcher: React.FC<{
  countries: Country[];
  extraFieldValues: ExtraFieldValues;
  onFieldsChange: AntsFormStepProps['onFieldsChange'];
}> = ({ countries, extraFieldValues, onFieldsChange }) => {
  const { values } = useFormikContext<AntsFormValues>();
  const lastPayloadRef = useRef<string | null>(null);

  useEffect(() => {
    const {
      nationalite, dateNaissance, villeNaissance, paysNaissance, adresse, complement, cp, ville, typeAdresse,
    } = values;
    const knownCountry = (value: string): string => (
      countries.some((country) => country.name === value) ? value : ''
    );
    const civilState = {
      nationalite: knownCountry(nationalite),
      dateNaissance,
      villeNaissance,
      paysNaissance: knownCountry(paysNaissance),
    };
    const address = {
      adresse, complement, cp, ville, typeAdresse,
    };
    const payload = JSON.stringify([civilState, address, extraFieldValues]);

    if (lastPayloadRef.current === null) {
      lastPayloadRef.current = payload;
      return;
    }
    if (payload === lastPayloadRef.current) {
      return;
    }

    lastPayloadRef.current = payload;
    onFieldsChange(civilState, address, extraFieldValues);
  }, [values, countries, extraFieldValues, onFieldsChange]);

  return null;
};

type AntsFormStepProps = {
  extraFields: DemarcheExtraField[];
  initialCivilState: CivilState;
  initialAddress: AddressInfo;
  initialExtraFieldValues: ExtraFieldValues;
  initialConsents: Consents;
  contactInfo: ContactInfo;
  isSubmitting?: boolean;
  submitErrorMessage?: string | null;
  // §F2a: null unless the account section's own submit call failed, in which case it may carry
  // `existing_email` (switches the form to the sign-in branch) or a field message under `password`.
  submitErrorCode?: string | null;
  passwordErrorMessage?: string | null;
  isSignedIn: boolean;
  signedInEmail?: string | null;
  // §5.7/§5.8: recomputed live server-side as nationalité/pays de naissance/type d'adresse/extra
  // fields change (see `onFieldsChange`) — this is `order.documentChecklist`, not derived here.
  documentChecklist: DemarcheDocument[];
  // §5.8 rule sets B/C: plain-French sentences the rules engine attaches to a checklist it just
  // recomputed (the hosting trio, the medical-exam note) — empty on a plain read, so this reflects
  // whatever create/update/refresh call produced the current `order`, not a persisted notice list.
  documentNotices: string[];
  uploadedDocuments: UploadedDocument[];
  documentsComplete: boolean;
  isDocumentsLoading?: boolean;
  uploadingDocumentKeys: Record<string, boolean>;
  documentUploadErrors: Record<string, string>;
  // Returns an error message on failure, null on success — DemarcheFunnelContainer owns the actual
  // POST /student_sessions call (via UserStore), this step only renders the form around it.
  onSignIn: (email: string, password: string) => Promise<string | null>;
  // Fired on every change to a field the document checklist depends on, so the container can
  // debounce a PATCH and keep section 4 live ahead of the final submit.
  onFieldsChange: (civilState: CivilState, address: AddressInfo, extraFieldValues: ExtraFieldValues) => void;
  onUploadDocument: (documentKey: string, file: File) => Promise<boolean>;
  onDeleteDocument: (id: string) => Promise<boolean>;
  onSubmit: (
    civilState: CivilState,
    address: AddressInfo,
    extraFieldValues: ExtraFieldValues,
    consents: Consents,
    password?: string,
  ) => void;
  onBack: () => void;
  onEditContact: () => void;
};

const AntsFormStep: React.FC<AntsFormStepProps> = ({
  extraFields,
  initialCivilState,
  initialAddress,
  initialExtraFieldValues,
  initialConsents,
  contactInfo,
  isSubmitting = false,
  submitErrorMessage = null,
  submitErrorCode = null,
  passwordErrorMessage = null,
  isSignedIn,
  signedInEmail = null,
  documentChecklist,
  documentNotices,
  uploadedDocuments,
  documentsComplete,
  isDocumentsLoading = false,
  uploadingDocumentKeys,
  documentUploadErrors,
  onSignIn,
  onFieldsChange,
  onUploadDocument,
  onDeleteDocument,
  onSubmit,
  onBack,
  onEditContact,
}) => {
  const [extraFieldValues, setExtraFieldValues] = useState<ExtraFieldValues>(initialExtraFieldValues);
  const [extraFieldErrors, setExtraFieldErrors] = useState<ExtraFieldValues>({});
  const [consents, setConsents] = useState<Consents>(initialConsents);
  const [consentErrors, setConsentErrors] = useState<Partial<Record<keyof Consents, string>>>({});
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signInError, setSignInError] = useState<string | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const {
    countries, isLoading: areCountriesLoading, hasError: hasCountriesError, retry: retryCountries,
  } = useCountries();
  const antsSchema = useMemo(
    () => buildAntsSchema(countries, hasCountriesError),
    [countries, hasCountriesError],
  );

  const isExistingEmailError = submitErrorCode === 'existing_email';

  useEffect(() => {
    if (isExistingEmailError) {
      setShowSignIn(true);
    }
  }, [isExistingEmailError]);

  const handleSignInClick = async (): Promise<void> => {
    setIsSigningIn(true);
    const error = await onSignIn(signInEmail, signInPassword);
    setSignInError(error);
    setIsSigningIn(false);
  };

  const initialValues: AntsFormValues = { ...initialCivilState, ...initialAddress };

  const validateExtraFields = (): ExtraFieldValues => extraFields.reduce((errors, field) => {
    if (isRequired(field) && !extraFieldValues[field.id]) {
      return { ...errors, [field.id]: REQUIRED_MESSAGE };
    }
    return errors;
  }, {} as ExtraFieldValues);

  const validateConsents = (): Partial<Record<keyof Consents, string>> => CONSENT_IDS.reduce(
    (errors, id) => {
      if (!consents[id]) {
        return { ...errors, [id]: CONSENT_REQUIRED_MESSAGE };
      }
      return errors;
    },
    {} as Partial<Record<keyof Consents, string>>,
  );

  const validatePassword = (): string | null => {
    if (isSignedIn) {
      return null;
    }
    if (!password) {
      return REQUIRED_MESSAGE;
    }
    if (password.length < 6) {
      return PASSWORD_TOO_SHORT_MESSAGE;
    }
    return null;
  };

  const handleSubmit = (values: AntsFormValues): void => {
    const errors = validateExtraFields();
    setExtraFieldErrors(errors);
    const consentErrs = validateConsents();
    setConsentErrors(consentErrs);
    const passwordErr = validatePassword();
    setPasswordError(passwordErr);
    if (Object.keys(errors).length > 0 || Object.keys(consentErrs).length > 0 || passwordErr) {
      return;
    }

    const civilState: CivilState = {
      nationalite: values.nationalite,
      dateNaissance: values.dateNaissance,
      villeNaissance: values.villeNaissance,
      paysNaissance: values.paysNaissance,
    };
    const address: AddressInfo = {
      adresse: values.adresse,
      complement: values.complement,
      cp: values.cp,
      ville: values.ville,
      typeAdresse: values.typeAdresse,
    };
    onSubmit(civilState, address, extraFieldValues, consents, isSignedIn ? undefined : password);
  };

  const handleFileSelected = (documentKey: string) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { target } = event;
    const file = target.files?.[0];
    target.value = '';
    if (!file) {
      return;
    }
    onUploadDocument(documentKey, file);
  };

  const missingRequiredDocs = documentChecklist.filter(
    (item) => item.required && !uploadedDocuments.some((doc) => doc.documentKey === item.id),
  );

  return (
    <Panel>
      <StepTitle>Vos informations</StepTitle>
      <StepText>
        Ces informations sont nécessaires pour préparer votre dossier auprès de France Titres — ANTS.
      </StepText>
      <Formik initialValues={initialValues} validationSchema={antsSchema} onSubmit={handleSubmit}>
        {({
          values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched,
        }) => (
          <FormikForm>
            <SectionTitle>1. Votre état civil</SectionTitle>
            <EditLink type="button" onClick={onEditContact}>
              {`${contactInfo.civilite === 'madame' ? 'Madame' : 'Monsieur'} ${contactInfo.prenom} `
                + `${contactInfo.nom} · ${contactInfo.email} · Modifier ces informations`}
            </EditLink>
            {hasCountriesError && (
              <ErrorBanner>
                {'Impossible de charger la liste des pays. '}
                <EditLink type="button" onClick={retryCountries}>Réessayer</EditLink>
              </ErrorBanner>
            )}
            <Fields>
              <CountryField
                id="nationalite"
                label="Nationalité*"
                value={values.nationalite}
                onChange={(value) => {
                  setFieldValue('nationalite', value);
                  setFieldTouched('nationalite', true, false);
                }}
                countries={countries}
                isLoading={areCountriesLoading}
                errorMessage={touched.nationalite ? errors.nationalite : ''}
              />
              <Field
                label="Date de naissance*"
                type="date"
                name="dateNaissance"
                autoComplete="bday"
                value={values.dateNaissance}
                onChange={handleChange}
                onBlur={handleBlur}
                valid={touched.dateNaissance ? !errors.dateNaissance : null}
                errorMessage={touched.dateNaissance ? errors.dateNaissance : ''}
              />
              <Field
                label="Ville de naissance*"
                placeholder="Ex : Nantes"
                name="villeNaissance"
                autoComplete="off"
                value={values.villeNaissance}
                onChange={handleChange}
                onBlur={handleBlur}
                valid={touched.villeNaissance ? !errors.villeNaissance : null}
                errorMessage={touched.villeNaissance ? errors.villeNaissance : ''}
              />
              <CountryField
                id="paysNaissance"
                label="Pays de naissance*"
                value={values.paysNaissance}
                onChange={(value) => {
                  setFieldValue('paysNaissance', value);
                  setFieldTouched('paysNaissance', true, false);
                }}
                countries={countries}
                isLoading={areCountriesLoading}
                errorMessage={touched.paysNaissance ? errors.paysNaissance : ''}
              />
            </Fields>

            {isSignedIn ? (
              <SignedInNote>{`Connecté en tant que ${signedInEmail}.`}</SignedInNote>
            ) : (
              <>
                {isExistingEmailError && (
                  <ExistingEmailNotice>
                    Un compte existe déjà avec cette adresse e-mail : connectez-vous ou
                    réinitialisez votre mot de passe ci-dessous.
                  </ExistingEmailNotice>
                )}
                <Fields>
                  <FullWidthRow>
                    <Field
                      label="Mot de passe*"
                      type="password"
                      name="password"
                      autoComplete="new-password"
                      placeholder="6 caractères minimum"
                      value={password}
                      onChange={(event) => {
                        setPassword(event.target.value);
                        setPasswordError(null);
                      }}
                      errorMessage={passwordError || passwordErrorMessage || ''}
                    />
                    <FieldHint>
                      Ce mot de passe donnera accès à votre espace élève, où votre dossier sera
                      suivi. Il doit comporter au moins 6 caractères.
                    </FieldHint>
                  </FullWidthRow>
                </Fields>
                {!showSignIn && (
                  <SignInToggleLink type="button" onClick={() => setShowSignIn(true)}>
                    Vous avez déjà un compte ? Se connecter
                  </SignInToggleLink>
                )}
                {showSignIn && (
                  <SignInBlock>
                    <Field
                      label="Adresse e-mail"
                      type="email"
                      name="signInEmail"
                      autoComplete="email"
                      placeholder="camille.durand@email.fr"
                      value={signInEmail}
                      onChange={(event) => {
                        setSignInEmail(event.target.value);
                        setSignInError(null);
                      }}
                    />
                    <Field
                      label="Mot de passe"
                      type="password"
                      name="signInPassword"
                      autoComplete="current-password"
                      placeholder="Votre mot de passe"
                      value={signInPassword}
                      onChange={(event) => {
                        setSignInPassword(event.target.value);
                        setSignInError(null);
                      }}
                      errorMessage={signInError || ''}
                    />
                    <FullWidthRow>
                      <Btn
                        type="button"
                        name="demarche-funnel-sign-in"
                        text="Se connecter"
                        outlined
                        isLoading={isSigningIn}
                        disabled={isSigningIn}
                        onClick={handleSignInClick}
                      />
                    </FullWidthRow>
                    <ForgotPasswordNote>
                      Mot de passe oublié ?
                      {' '}
                      {' '}
                      <a href={FORGOT_PASSWORD_URL}>Réinitialisez-le</a>
                      {' '}
                      {' '}
                      — vous quitterez cette page et les informations déjà saisies ne seront
                      pas conservées.
                    </ForgotPasswordNote>
                  </SignInBlock>
                )}
              </>
            )}

            <SectionTitle>2. Votre adresse</SectionTitle>
            <Fields>
              <FullWidthRow>
                <Field
                  label="Adresse*"
                  placeholder="N° et nom de voie"
                  name="adresse"
                  autoComplete="address-line1"
                  value={values.adresse}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  valid={touched.adresse ? !errors.adresse : null}
                  errorMessage={touched.adresse ? errors.adresse : ''}
                />
              </FullWidthRow>
              <FullWidthRow>
                <Field
                  label="Complément d’adresse (facultatif)"
                  name="complement"
                  autoComplete="address-line2"
                  value={values.complement}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FullWidthRow>
              <Field
                label="Code postal*"
                placeholder="75001"
                name="cp"
                autoComplete="postal-code"
                value={values.cp}
                onChange={handleChange}
                onBlur={handleBlur}
                valid={touched.cp ? !errors.cp : null}
                errorMessage={touched.cp ? errors.cp : ''}
              />
              <Field
                label="Ville*"
                placeholder="Paris"
                name="ville"
                autoComplete="address-level2"
                value={values.ville}
                onChange={handleChange}
                onBlur={handleBlur}
                valid={touched.ville ? !errors.ville : null}
                errorMessage={touched.ville ? errors.ville : ''}
              />
              <FullWidthRow>
                <FieldLabel id="type-adresse-label">Concernant cette adresse*</FieldLabel>
                <RadioChoices role="radiogroup" aria-labelledby="type-adresse-label">
                  {ADDRESS_TYPE_OPTIONS.map((option) => (
                    <RadioChoice key={option.value} isSelected={values.typeAdresse === option.value}>
                      <input
                        type="radio"
                        name="typeAdresse"
                        value={option.value}
                        checked={values.typeAdresse === option.value}
                        onChange={() => {
                          setFieldValue('typeAdresse', option.value);
                          setFieldTouched('typeAdresse', true, false);
                        }}
                      />
                      <span>{option.label}</span>
                    </RadioChoice>
                  ))}
                </RadioChoices>
                <FieldError>{touched.typeAdresse && errors.typeAdresse}</FieldError>
              </FullWidthRow>
            </Fields>

            <SectionTitle>3. Informations complémentaires</SectionTitle>
            <DynamicFieldsForm
              fields={extraFields}
              values={extraFieldValues}
              errors={extraFieldErrors}
              onChange={setExtraFieldValues}
            />

            <ChecklistWatcher countries={countries} extraFieldValues={extraFieldValues} onFieldsChange={onFieldsChange} />

            <SectionTitle>4. Documents à télécharger</SectionTitle>
            <DocumentsIntro>
              Formats acceptés : JPG, PNG ou PDF. Les pièces marquées d’un astérisque sont
              obligatoires. Après examen de votre dossier, des documents complémentaires pourront
              vous être demandés en fonction de votre situation.
            </DocumentsIntro>
            {documentNotices.map((notice) => (
              <FieldHint key={notice}>{notice}</FieldHint>
            ))}
            {isDocumentsLoading && documentChecklist.length === 0 ? (
              <StepText>Chargement des pièces à fournir…</StepText>
            ) : (
              documentChecklist.map((item) => {
                const filesForDoc = uploadedDocuments.filter((doc) => doc.documentKey === item.id);
                const isUploading = Boolean(uploadingDocumentKeys[item.id]);
                const canAddMore = filesForDoc.length < MAX_FILES_PER_DOCUMENT;

                return (
                  <UploadRow key={item.id}>
                    <div>
                      <UploadRowLabel>{`${item.label}${item.required ? '*' : ''}`}</UploadRowLabel>
                      {item.hint && <UploadRowHint>{item.hint}</UploadRowHint>}
                      {filesForDoc.length > 0 && (
                        <UploadRowFiles>
                          {filesForDoc.map((doc) => (
                            <UploadedFile key={doc.id}>
                              {`✓ ${doc.filename}`}
                              <RemoveFileButton
                                type="button"
                                onClick={() => onDeleteDocument(doc.id)}
                              >
                                Supprimer
                              </RemoveFileButton>
                            </UploadedFile>
                          ))}
                        </UploadRowFiles>
                      )}
                      <FieldError>{documentUploadErrors[item.id]}</FieldError>
                    </div>
                    <UploadRowActions>
                      {canAddMore && (
                        <>
                          <Btn
                            type="button"
                            name={`demarche-funnel-upload-${item.id}`}
                            text="Choisir un fichier"
                            outlined
                            small
                            isLoading={isUploading}
                            disabled={isUploading}
                            onClick={() => fileInputRefs.current[item.id]?.click()}
                          />
                          <HiddenFileInput
                            type="file"
                            accept={DOCUMENT_FILE_ACCEPT}
                            ref={(el: HTMLInputElement | null) => { fileInputRefs.current[item.id] = el; }}
                            onChange={handleFileSelected(item.id)}
                          />
                        </>
                      )}
                    </UploadRowActions>
                  </UploadRow>
                );
              })
            )}
            {missingRequiredDocs.length > 0 && (
              <MissingDocumentsNotice>
                {'Il reste à fournir : '}
                {missingRequiredDocs.map((item) => item.label).join(', ')}
                .
              </MissingDocumentsNotice>
            )}

            <SectionTitle>5. Consentements</SectionTitle>
            {consentItemsFor(isMinorBirthDate(values.dateNaissance)).map((item) => (
              <FullWidthRow key={item.id}>
                <ConsentRow>
                  <input
                    type="checkbox"
                    checked={consents[item.id]}
                    onChange={(event) => {
                      setConsents({ ...consents, [item.id]: event.target.checked });
                      setConsentErrors((currentErrors) => {
                        const rest = { ...currentErrors };
                        delete rest[item.id];
                        return rest;
                      });
                    }}
                  />
                  <span>{item.label}</span>
                </ConsentRow>
                <FieldError>{consentErrors[item.id]}</FieldError>
              </FullWidthRow>
            ))}

            <Actions>
              <Btn type="button" name="demarche-funnel-ants-back" text="Retour" outlined block onClick={onBack} />
              <Btn
                type="submit"
                name="demarche-funnel-ants-submit"
                text="Continuer"
                block
                isLoading={isSubmitting}
                disabled={isSubmitting || isDocumentsLoading || !documentsComplete}
              />
            </Actions>
            {submitErrorMessage && <ErrorBanner>{submitErrorMessage}</ErrorBanner>}
          </FormikForm>
        )}
      </Formik>
    </Panel>
  );
};

export { AntsFormStep };
